import { Button, Checkbox, Form, Input, Progress, Select, Spin, Tooltip } from 'antd';
import clsx from 'clsx';
import { type ReactNode, useState } from 'react';
import QuickShareFilesList from '../../components/Common/QuickShareFilesList';
import UserMultiSelect from '../../components/Common/UserMultiSelect';
import { Icon } from '../../components/Core/Icon';
import { QuickShareStatusEnum } from '../../enums/QuickShareStatusEnum';
import { UserRoleEnum } from '../../enums/UserRoleEnum';
import { useAuth } from '../../hooks/useAuth';
import { useBranding } from '../../hooks/useBranding';
import { usePermissions } from '../../hooks/usePermissions';
import useQuickShare from '../../hooks/useQuickShare';
import {
  getQuickShareExpirationOptionsFiltered,
  QUICK_SHARE_EXPIRATION_DEFAULT_DAYS,
} from '../../lib/quickShareExpirationOptions';
import { getBrandingDataDefaultImage } from '../../utils/utils';

export const QuickShare = () => {
  const {
    t,
    handleAddFiles,
    handleSubmit,
    handleRemoveFile,
    isUploadingQuickShareFiles,
    handleQuickShareInputChange,
    quickShareForm,
    isSubmitButtonDisabled,
    currentUploadStatus,
    isFormDisabled,
    submitButtonLabel,
    isCancelButtonVisible,
    handleCancel,
    failedUploadQuickShareFiles,
    retryFailedUploads,
    handleShareQuickShareFiles,
    isPageLoaded,
    areNewUsersOrGroupsSelected,
    quickShareGlobalProgressByBytes,
    uploadFilesList,
    isNetworkAvailable,
  } = useQuickShare();

  const { authenticatedUser } = useAuth();

  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const { fileSizeLimitUpload } = usePermissions();
  const { brandingData, isLoadingBrandingData } = useBranding();

  const shouldShowSpinner =
    isLoadingBrandingData || !isPageLoaded || !brandingData || !isImageLoaded;

  return (
    <div className="flex h-full flex-1">
      <div className="relative hidden w-0 max-w-[600px] flex-1 items-center justify-center lg:flex">
        {shouldShowSpinner && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/40 backdrop-blur-sm">
            <Spin size="large" />
          </div>
        )}

        {brandingData && (
          <img
            alt="Quick share background"
            src={
              brandingData.quickShare
                ? `${brandingData.quickShare}?v=${Date.now()}`
                : getBrandingDataDefaultImage('quick-share')
            }
            loading="eager"
            onLoad={() => setIsImageLoaded(true)}
            className={clsx(
              'inset-0 size-full h-full object-cover transition-opacity duration-500',
              isImageLoaded ? 'opacity-100' : 'opacity-0',
            )}
          />
        )}
      </div>
      <div className="flex flex-1 grow flex-col justify-center overflow-scroll px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto flex w-full max-w-lg flex-col gap-2">
          <h1 className="text-blue-dark pb-8 text-center text-4xl leading-6 font-medium">
            Quick Share
          </h1>
          {currentUploadStatus === QuickShareStatusEnum.Pending ? (
            <div
              className="border-azure bg-azure-hover/5 focus:bg-azure-light grid w-full flex-none cursor-pointer grid-cols-2 rounded-2xl border-2 border-dashed transition-all duration-200 ease-in-out"
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.style.filter = 'opacity(100%)';
                handleAddFiles(e.dataTransfer.items);
              }}
              onDragOver={(e) => {
                // Change bg color when dragging over by adding a class
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
                e.currentTarget.style.filter = 'opacity(70%)';
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.currentTarget.style.filter = 'opacity(100%)';
              }}
            >
              <label
                htmlFor="dropzone-file"
                className="hover:bg-azure-light flex cursor-pointer items-center justify-center rounded-2xl py-4 transition-all duration-200 ease-in-out"
              >
                <div className="flex flex-col items-center justify-center gap-1">
                  <Icon name="FileUpIcon" className="text-azure h-24 w-24" />
                  <span className="font-medium">{t('common.addFiles')}</span>
                  <p
                    className={clsx(
                      'text-gray-dark text-xxs transition-all duration-200 ease-in-out',
                      isLoadingBrandingData && 'blur-sm',
                    )}
                  >
                    {t('common.max')} {fileSizeLimitUpload}MB
                  </p>
                </div>
                <input
                  id="dropzone-file"
                  onChange={(e) => {
                    if (e.target.files) {
                      handleAddFiles(e.target.files);
                      e.target.value = '';
                    }
                  }}
                  multiple
                  type="file"
                  className="hidden"
                />
              </label>
              <label
                htmlFor="dropzone-folder"
                className="hover:bg-azure-light flex cursor-pointer items-center justify-center rounded-2xl py-4 transition-all duration-200 ease-in-out"
              >
                <div className="flex flex-col items-center justify-center gap-1">
                  <Icon name="FolderUpIcon" className="text-azure h-24 w-24" />
                  <span className="font-medium">{t('common.addFolders')}</span>
                  <p
                    className={clsx(
                      'text-gray-dark text-xxs transition-all duration-200 ease-in-out',
                      isLoadingBrandingData && 'blur-sm',
                    )}
                  >
                    {t('common.maxFilesAmountAndSizeEach', {
                      amount: '500.000',
                      size: fileSizeLimitUpload,
                    })}
                  </p>
                </div>
                <input
                  id="dropzone-folder"
                  onChange={(e) => {
                    if (e.target.files) {
                      handleAddFiles(e.target.files);
                      e.target.value = '';
                    }
                  }}
                  multiple
                  webkitdirectory="true"
                  mozdirectory="true"
                  type="file"
                  className="hidden"
                />
              </label>
            </div>
          ) : (
            <div
              className={clsx(
                'flex h-48 flex-none flex-col items-center justify-center gap-1 rounded-2xl border-2 transition-all duration-200 ease-in-out',
                currentUploadStatus === QuickShareStatusEnum.Uploading &&
                  'border-azure-hover bg-azure-hover/5',
                [QuickShareStatusEnum.ConnectionError, QuickShareStatusEnum.Error].includes(
                  currentUploadStatus,
                ) && 'border-red-dark bg-red-dark/5',
                [QuickShareStatusEnum.SomeErrors, QuickShareStatusEnum.ShareError].includes(
                  currentUploadStatus,
                ) && 'border-yellow-dark bg-yellow-dark/5',
                currentUploadStatus === QuickShareStatusEnum.Completed && 'border-green bg-green/5',
              )}
            >
              {((): ReactNode => {
                switch (currentUploadStatus) {
                  case QuickShareStatusEnum.Uploading:
                    return (
                      <>
                        {!isNetworkAvailable ? (
                          <>
                            <Icon name="UnplugIcon" className="text-red-dark mb-2 h-20 w-20" />
                            <h2 className="text-gray-dark text-center font-medium">
                              {t('common.uploadsPausedWaitingForNetwork')}
                            </h2>
                          </>
                        ) : (
                          <>
                            <Progress
                              type="circle"
                              percent={quickShareGlobalProgressByBytes}
                              size={40}
                            />
                            <h2 className="text-gray-dark font-medium">Uploading files...</h2>
                          </>
                        )}
                        <Button
                          aria-label={t('common.cancel')}
                          type="text"
                          className="text-azure underline"
                          onClick={handleCancel}
                        >
                          {t('common.cancel')}
                        </Button>
                      </>
                    );
                  case QuickShareStatusEnum.ConnectionError:
                    return (
                      <>
                        <Icon name="UnplugIcon" className="text-red-dark mb-2 h-20 w-20" />
                        <h2 className="text-gray-dark text-center font-medium">
                          {isUploadingQuickShareFiles
                            ? t('common.uploadsPausedWaitingForNetwork')
                            : 'A connection error occurred'}
                        </h2>
                        {!isUploadingQuickShareFiles && (
                          <Button
                            aria-label={t('common.retry')}
                            type="text"
                            className="text-azure underline"
                            onClick={() => {
                              retryFailedUploads();
                            }}
                          >
                            {t('common.retry')}
                          </Button>
                        )}
                      </>
                    );
                  case QuickShareStatusEnum.Error:
                    return (
                      <>
                        <Icon name="ServerCrashIcon" className="text-red-dark mb-2 h-20 w-20" />
                        <h2 className="text-gray-dark text-center font-medium">
                          Something went wrong with our servers or you've selected all wrong files
                        </h2>
                        <Button
                          aria-label={t('common.retry')}
                          type="text"
                          className="text-azure underline"
                          onClick={() => {
                            retryFailedUploads();
                          }}
                        >
                          {t('common.retry')}
                        </Button>
                      </>
                    );
                  case QuickShareStatusEnum.SomeErrors:
                    return (
                      <>
                        <Icon
                          name="TriangleAlertIcon"
                          className="text-yellow-dark mb-2 h-20 w-20"
                        />
                        <h2 className="text-gray-dark text-center font-medium">
                          {t('errors.quickShareFilesNotUploaded', {
                            count: failedUploadQuickShareFiles.length,
                          })}
                        </h2>
                      </>
                    );
                  case QuickShareStatusEnum.ShareError:
                    return (
                      <>
                        <Icon
                          name="TriangleAlertIcon"
                          className="text-yellow-dark mb-2 h-20 w-20"
                        />
                        <h2 className="text-gray-dark text-center font-medium">
                          {t('errors.quickShareFilesNotShared')}
                        </h2>
                        <Button
                          aria-label={t('common.retry')}
                          type="text"
                          className="text-azure underline"
                          onClick={() => {
                            handleShareQuickShareFiles();
                          }}
                        >
                          {t('common.retry')}
                        </Button>
                      </>
                    );
                  case QuickShareStatusEnum.Completed:
                    return (
                      <>
                        <Icon name="CircleCheckIcon" className="text-green mb-2 h-20 w-20" />
                        <h2 className="text-gray-dark text-center font-medium">
                          Files successfully shared
                        </h2>
                      </>
                    );
                  default:
                    return 'N/D';
                }
              })()}
            </div>
          )}

          <QuickShareFilesList files={uploadFilesList} handleRemoveFile={handleRemoveFile} />
          <h2 className="text-blue-dark text-xl font-semibold">{t('common.shareWith')}...</h2>
          <Form
            aria-label={t('common.shareWith')}
            className="mx-auto flex w-full flex-col gap-2.5 transition-all duration-200 ease-in-out"
          >
            <div
              className={clsx(
                'divide-gray-medium flex w-full flex-row items-center divide-x rounded-md',
                areNewUsersOrGroupsSelected ? 'border-gray-medium border' : 'border-transparent',
              )}
            >
              <UserMultiSelect
                className="w-full"
                mode="tags"
                closeOnSelect
                variant={areNewUsersOrGroupsSelected ? 'borderless' : 'outlined'}
                size="large"
                maxTagCount={10}
                allowClear={false}
                disabled={isFormDisabled}
                ignoredUserIds={[authenticatedUser?.id || 0]}
                forbiddenTagNames={[authenticatedUser?.email || '']}
                onUserChange={(_, values) => handleQuickShareInputChange('users', values)}
                onGroupChange={(_, values) => handleQuickShareInputChange('groups', values)}
                additionalUserFilters={{
                  roleFilter: [
                    UserRoleEnum.SuperAdmin,
                    UserRoleEnum.Admin,
                    UserRoleEnum.Contributor,
                    UserRoleEnum.Reader,
                  ],
                }}
                selectedUserIds={quickShareForm.users
                  .map((user) => user?.id)
                  .filter((id): id is number => typeof id === 'number')}
                selectedGroupIds={quickShareForm.groups
                  .map((group) => group?.id)
                  .filter((id): id is number => typeof id === 'number')}
                selectedUsers={quickShareForm.users}
                selectedGroups={quickShareForm.groups}
              />

              {areNewUsersOrGroupsSelected && (
                <Select
                  className="p-0"
                  variant="borderless"
                  placement="bottomRight"
                  disabled={isFormDisabled}
                  value={quickShareForm.permission}
                  onChange={(value) => handleQuickShareInputChange('permission', value)}
                  optionRender={(option) => {
                    return (
                      <div className="flex w-full min-w-32 flex-row items-center gap-2">
                        {option.data.icon}
                        <span>{option.data.label}</span>
                      </div>
                    );
                  }}
                  popupMatchSelectWidth={false}
                  labelRender={() => {
                    return (
                      <Tooltip
                        title={quickShareForm.permission ? t('common.viewer') : t('common.editor')}
                      >
                        <div>
                          <Icon
                            name={quickShareForm.permission === '0' ? 'ViewIcon' : 'SquarePenIcon'}
                            className="h-6 w-6"
                          />
                        </div>
                      </Tooltip>
                    );
                  }}
                  options={[
                    {
                      value: '0',
                      icon: <Icon name="ViewIcon" className="h-5 w-5" />,
                      label: t('common.viewer'),
                    },
                    {
                      value: '1',
                      icon: <Icon name="SquarePenIcon" className="h-5 w-5" />,
                      label: t('common.editor'),
                    },
                  ]}
                />
              )}
            </div>
            <Select
              size="large"
              className="w-full"
              aria-label={t('common.expiration')}
              disabled={isFormDisabled}
              value={(() => {
                const maxDays = authenticatedUser?.qsDays ?? QUICK_SHARE_EXPIRATION_DEFAULT_DAYS;
                const allowed = getQuickShareExpirationOptionsFiltered(maxDays);
                const current = parseInt(quickShareForm.expiration, 10);
                const valid = Number.isFinite(current) && allowed.includes(current);
                return valid
                  ? String(current)
                  : String(allowed[allowed.length - 1] ?? QUICK_SHARE_EXPIRATION_DEFAULT_DAYS);
              })()}
              onChange={(value) => handleQuickShareInputChange('expiration', value)}
              options={(() => {
                const maxDays = authenticatedUser?.qsDays ?? QUICK_SHARE_EXPIRATION_DEFAULT_DAYS;
                return getQuickShareExpirationOptionsFiltered(maxDays).map((days) => ({
                  value: String(days),
                  label: t('common.day', { count: days }),
                }));
              })()}
            />
            {!isSubmitButtonDisabled && (
              <>
                <Checkbox
                  aria-label="Has message"
                  disabled={isFormDisabled}
                  checked={quickShareForm.hasMessage}
                  onChange={(e) => {
                    handleQuickShareInputChange('hasMessage', e.target.checked);
                  }}
                >
                  {t('common.sendMessage')}
                </Checkbox>
                {quickShareForm.hasMessage && (
                  <Input.TextArea
                    aria-label={t('common.message')}
                    disabled={isFormDisabled}
                    rows={4}
                    placeholder={t('common.quickShareMessagePlaceholder')}
                    onChange={(e) => {
                      handleQuickShareInputChange('message', e.target.value);
                    }}
                    value={quickShareForm.message}
                  />
                )}
              </>
            )}
            <div
              className={clsx('grid gap-4', isCancelButtonVisible ? 'grid-cols-2' : 'grid-cols-1')}
            >
              {isCancelButtonVisible && (
                <Button type="text" aria-label={t('common.cancel')} onClick={handleCancel}>
                  {t('common.cancel')}
                </Button>
              )}
              <Button
                type="primary"
                size="large"
                aria-label={submitButtonLabel}
                disabled={isSubmitButtonDisabled}
                loading={isUploadingQuickShareFiles}
                onClick={handleSubmit}
              >
                {submitButtonLabel}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default QuickShare;
